/* Native Shopify customer forms; progressive enhancement only. No form fetch,
   fabricated entry receipts, email addresses in storage, or monthly timestamps. */
(() => {
  if (customElements.get('hans-email-capture')) return;

  const keys = {
    shown: 'hans:giveaway:v4:shown',
    dismissed: 'hans:giveaway:v4:dismissed-until',
    received: 'hans:giveaway:v4:received',
    pending: 'hans:giveaway:v4:pending'
  };
  const memory = new Map();
  const stores = {};
  for (const name of ['localStorage', 'sessionStorage']) {
    try {
      const store = window[name];
      const probe = 'hans:giveaway:v4:storage-check';
      store.setItem(probe, '1');
      store.removeItem(probe);
      stores[name] = store;
    } catch (_) { stores[name] = null; }
  }
  const read = (key) => {
    for (const store of [stores.sessionStorage, stores.localStorage]) {
      try {
        const value = store?.getItem(key);
        if (value !== null && value !== undefined) return value;
      } catch (_) { /* In-memory fallback keeps this page usable. */ }
    }
    return memory.get(key) || null;
  };
  const write = (key, value, persistent = false) => {
    memory.set(key, value);
    try { stores.sessionStorage?.setItem(key, value); } catch (_) { /* Optional. */ }
    if (persistent) {
      try { stores.localStorage?.setItem(key, value); } catch (_) { /* Optional. */ }
    }
  };
  const remove = (key) => {
    memory.delete(key);
    for (const store of [stores.sessionStorage, stores.localStorage]) {
      try { store?.removeItem(key); } catch (_) { /* Optional. */ }
    }
  };
  const pending = () => {
    try {
      const item = JSON.parse(read(keys.pending));
      return item && Date.now() - item.at < 30 * 60 * 1000 ? item : null;
    } catch (_) { return null; }
  };
  const visible = (node) => {
    if (!node || node.hidden || !node.getClientRects().length) return false;
    const style = getComputedStyle(node);
    return style.display !== 'none' && style.visibility !== 'hidden';
  };
  const otherOverlay = (ownDialog) => [...document.querySelectorAll(
    'dialog[open], x-drawer[open], x-modal[open], x-popover[open], cart-drawer[open], cart-notification-drawer[open], newsletter-popup[open], privacy-bar[open], [aria-modal="true"], .shopify-pc__banner, #shopify-pc__banner, .shopify-pc__prefs, #shopify-pc__prefs'
  )].some((node) => node !== ownDialog && !ownDialog?.contains(node) && visible(node));

  class HansEmailCapture extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', () => {
        // Only keep placement metadata. The browser/Shopify still handles POST,
        // CAPTCHA, validation and subscription confirmation normally.
        write(keys.pending, JSON.stringify({ id: this.form.id, source: this.dataset.source, at: Date.now() }));
      }, { signal: this.abort.signal });
      const response = this.querySelector('[data-capture-response]');
      if (!response) return;
      const fromThisForm = location.hash === `#${this.form.id}` || pending()?.id === this.form.id;
      if (response.dataset.captureResponse === 'success') {
        // This marker exists only inside Liquid form.posted_successfully?.
        write(keys.received, '1', true);
        document.dispatchEvent(new CustomEvent('hans:giveaway:received', { detail: { source: this.dataset.source, fromThisForm } }));
      }
      if (fromThisForm && this.dataset.source === 'footer') {
        requestAnimationFrame(() => response.focus());
      }
    }
    disconnectedCallback() { this.abort?.abort(); }
  }

  class HansGiveawayPopup extends HTMLElement {
    connectedCallback() {
      this.disconnect();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.activeSeconds = 0;
      this.hasScrolled = false;
      this.pauseUntil = Date.now() + 2000;
      this.lastTick = performance.now();
      this.preview = this.dataset.designMode === 'true';
      this.querySelector('[data-capture-close]')?.addEventListener('click', () => this.close(), { signal });
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, { signal });
      this.dialog.addEventListener('close', () => {
        this.hidden = true;
        if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
      }, { signal });
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close();
      }, { signal });
      // Native modal dialogs make the page inert; this also cycles focus in
      // browsers where tab traversal would otherwise include browser chrome.
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const items = [...this.dialog.querySelectorAll('button:not([disabled]), input:not([disabled]):not([type="hidden"]), a[href], select, textarea, [tabindex="0"]')].filter(visible);
        if (!items.length) { event.preventDefault(); return; }
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
      }, { signal });
      window.addEventListener('scroll', () => {
        if (window.scrollY >= Math.min(240, window.innerHeight * 0.35)) this.hasScrolled = true;
      }, { passive: true, signal });
      document.addEventListener('pointerdown', () => { this.pauseUntil = Date.now() + 2000; }, { passive: true, signal });
      document.addEventListener('submit', (event) => {
        if (!event.target.closest('hans-email-capture')) this.pauseUntil = Date.now() + 15000;
      }, { signal });
      document.addEventListener('hans:giveaway:received', (event) => {
        if (event.detail?.fromThisForm && event.detail.source === 'footer') this.close({ suppress: false });
      }, { signal });
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.show({ preview: true });
      }, { signal });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.close({ suppress: false });
      }, { signal });

      // A native POST return may contain success/errors in more than one
      // customer form. Only reopen this result when the submitted source was
      // this popup. Never infer success from the URL or a pending submission.
      const popupForm = this.querySelector('form');
      const response = this.querySelector('[data-capture-response]');
      const fromPopup = popupForm && (location.hash === `#${popupForm.id}` || pending()?.id === popupForm.id);
      this.returningResult = Boolean(response && fromPopup);
      if (this.returningResult) remove(keys.pending);
      this.timer = window.setInterval(() => this.tick(), 500);
      this.tick();
    }
    disconnect() {
      if (this.timer) window.clearInterval(this.timer);
      this.abort?.abort();
      if (this.dialog?.open) this.dialog.close();
    }
    disconnectedCallback() { this.disconnect(); }
    suppressed() {
      return this.dataset.enabled !== 'true' || this.dataset.knownCustomer === 'true' ||
        read(keys.received) === '1' || read(keys.shown) === '1' || Number(read(keys.dismissed)) > Date.now();
    }
    busy() {
      return Date.now() < this.pauseUntil || otherOverlay(this.dialog) ||
        Boolean(document.activeElement?.closest('input, textarea, select, [contenteditable="true"]'));
    }
    tick() {
      const now = performance.now();
      const elapsed = Math.min((now - this.lastTick) / 1000, 1);
      this.lastTick = now;
      if (document.visibilityState !== 'visible' || !document.hasFocus() || this.dialog.open) return;
      if (this.returningResult && !this.busy()) {
        this.returningResult = false;
        this.show({ result: true });
        return;
      }
      if (this.preview || this.suppressed()) return;
      // If persistence is blocked entirely, do not surprise the shopper with
      // the same automatic popup on every page. Footer capture still works.
      if (!stores.localStorage && !stores.sessionStorage) return;
      this.activeSeconds += elapsed;
      if (this.activeSeconds >= (Number(this.dataset.delay) || 15) && this.hasScrolled && !this.busy()) this.show();
    }
    show({ preview = false, result = false } = {}) {
      if (!this.dialog || this.dialog.open || otherOverlay(this.dialog)) return false;
      if (!preview && !result && this.suppressed()) return false;
      this.previousFocus = document.activeElement;
      this.hidden = false;
      this.openedAsPreview = preview;
      try { this.dialog.showModal(); } catch (_) { this.hidden = true; return false; }
      if (!preview) write(keys.shown, '1');
      // Announce the offer without summoning the mobile software keyboard.
      const focusTarget = this.querySelector('[data-capture-response]') || this.querySelector('[data-capture-heading]') || this.querySelector('[data-capture-close]');
      requestAnimationFrame(() => focusTarget?.focus({ preventScroll: true }));
      return true;
    }
    close({ suppress = true } = {}) {
      if (!this.dialog?.open) return;
      if (suppress && !this.preview && !this.openedAsPreview) {
        const days = Number(this.dataset.dismissDays) || 14;
        write(keys.dismissed, String(Date.now() + days * 86400000), true);
      }
      this.returningResult = false;
      this.dialog.close();
    }
  }
  customElements.define('hans-email-capture', HansEmailCapture);
  customElements.define('hans-giveaway-popup', HansGiveawayPopup);
})();
